import React, { useState, useEffect, useRef } from "react";
import '../../assets/css/UC/multipleImages.scss';
import '../../config';
import { getThumbnailUrl } from '../../utils/imageUrlBuilder';
import { sortImagesByPriority } from '../../utils/imageSorting';

export default function MultipleImages({ images, handleZoomImage, defaultIndex, onImageChange, isAnalyze = false, title = "", imageScores = null }) {
    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(defaultIndex);
    const [sortedImages, setSortedImages] = useState([]);
    const isInitialLoad = useRef(true);
    const userInteracted = useRef(false);

    const svgStandardize = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" fill="#28a745" stroke="" stroke-width="2"/>
        <path d="M9 12l2 2 4-4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;

    useEffect(() => {

        if (sortedImages.length > 0 && (isInitialLoad.current || !userInteracted.current)) {
            const targetIndex = Math.min(defaultIndex || 0, sortedImages.length - 1);
            const targetImage = sortedImages[targetIndex];
            setSelectedImage(targetImage);
            setSelectedIndex(targetIndex);

            // Notify parent component with the correct index
            if (onImageChange) {

                onImageChange(targetIndex, targetImage);
            }

            isInitialLoad.current = false;
        }
    }, [sortedImages, defaultIndex, onImageChange]);


    // Initialize selected image based on defaultIndex prop and sort images
    useEffect(() => {
        // Reset flags when images array actually changes (not just re-renders)
        isInitialLoad.current = true;
        userInteracted.current = false;

        if (images && images.length > 0) {
            let sorted = sortImagesByPriority(images, isAnalyze);

            // Attach scores if provided (MultipleImages-specific)
            if (imageScores && Array.isArray(imageScores)) {
                sorted = sorted.map(img => {
                    const scoreEntry = imageScores.find(score => score.ImageID === img.IDValue);
                    return scoreEntry ? { ...img, Score: scoreEntry.Score } : img;
                });
            }

            setSortedImages(sorted);
        } else {
            setSortedImages([]);
            setSelectedImage(null);
            setSelectedIndex(0);
            isInitialLoad.current = true; // Reset for next time images are loaded
        }
    }, [images, isAnalyze, defaultIndex]);

    // Image URL helper — delegated to shared utility
    const getLinkImage = getThumbnailUrl;

    // Get score for an image from imageScores prop
    const getImageScore = (imageId) => {
        if (!imageScores || !Array.isArray(imageScores)) return null;
        const scoreEntry = imageScores.find(score => score.ImageID === imageId);
        return scoreEntry ? scoreEntry.Score : null;
    };

    const getImageScoreClass = (score) => {
        if (score === null || score === undefined) return '';
        // if (score >= 8) return 'high-score';
        // if (score >= 5) return 'medium-score';
        return 'score';
    }

    const handleThumbnailClick = (image, sortedIndex) => {
        // Mark that user has interacted with the component
        userInteracted.current = true;

        // Update local state
        setSelectedImage(image);
        setSelectedIndex(sortedIndex);

        // Immediately notify parent component with the correct index
        if (onImageChange)
            onImageChange(sortedIndex);
    };

    const convertImageTypeToName = (type) => {
        
        switch (type) {
            case global.config.Defines.ImageTypeKey.SHOE_SOLE:
                return global.config.Defines.ImageTypeName.SHOE_SOLE;
            case global.config.Defines.ImageTypeKey.SHOE_SOLE_LEFT:
                return global.config.Defines.ImageTypeName.SHOE_SOLE_LEFT;
            case global.config.Defines.ImageTypeKey.SHOE_SOLE_RIGHT:
                return global.config.Defines.ImageTypeName.SHOE_SOLE_RIGHT;
            case global.config.Defines.ImageTypeKey.SHOE_SOLE_UNDETERMINED:
                return global.config.Defines.ImageTypeName.SHOE_SOLE_UNDETERMINED;

            case global.config.Defines.ImageTypeKey.SHOE_PRINT:
                return global.config.Defines.ImageTypeName.SHOE_PRINT;
            case global.config.Defines.ImageTypeKey.SHOE_PRINT_LEFT:
                return global.config.Defines.ImageTypeName.SHOE_PRINT_LEFT;
            case global.config.Defines.ImageTypeKey.SHOE_PRINT_RIGHT:
                return global.config.Defines.ImageTypeName.SHOE_PRINT_RIGHT;
            case global.config.Defines.ImageTypeKey.SHOE_PRINT_UNDETERMINED:
                return global.config.Defines.ImageTypeName.SHOE_PRINT_UNDETERMINED;

            default:
                return global.config.Defines.ImageTypeName.SHOE_OTHER;
        }
    };

    return (
        <>
            <div id="glImages" className="gallery-images-container">
                {isAnalyze && selectedImage && (
                    <>
                        <div className="gallery-title">
                            <h4>{title}</h4>
                        </div>
                    </>
                )}
                <div className="overview-image-wrapper">
                    {selectedImage && selectedImage.S3RelativePath ? (
                        <>
                            <img
                                className="overview-image"
                                src={getLinkImage(selectedImage)}
                                alt={convertImageTypeToName(selectedImage.Type) || "Overview"}
                            />
                            <div className="image-type-overlay" hidden={!selectedImage.Type}>
                                {selectedImage.Type && (
                                    <span className="image-type-badge">
                                        {convertImageTypeToName(selectedImage.Type) || 'Unknown Type'}
                                    </span>
                                )}
                            </div>
                            {selectedImage.IsStandardize === 1 && (
                                <div className="standardize-icon-overlay" title="Standardized Image">
                                    <div dangerouslySetInnerHTML={{ __html: svgStandardize }} />
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="overview-image-placeholder">Select an image</div>
                    )}
                    {selectedImage && (
                        <div className="zoom-icon-overlay" onClick={handleZoomImage} title="Zoom Image">
                            <i className="fa fa-search-plus" aria-hidden="true"></i>
                        </div>
                    )}
                </div>

                {sortedImages.length > 1 && (
                    <div className="gallery-thumbnails-wrapper">
                        <ul className={`gallery-thumbnails-list ${isAnalyze ? 'analyze-mode' : ''} ${imageScores ? 'with-scores' : ''}`}>

                            {sortedImages.map((imgInfo, index) => (
                                <li
                                    key={imgInfo.IDValue || index}
                                    className={`thumbnail-item ${index === selectedIndex ? 'active' : ''} ${getImageScoreClass(imgInfo.Score)}`}
                                    onClick={() => handleThumbnailClick(imgInfo, index)}
                                    title={convertImageTypeToName(imgInfo.Type) || `Image ${index + 1}`}
                                >
                                    {imgInfo.Score && (
                                        <div className="thumbnail-score-badge" title={`Score: ${imgInfo.Score?.toFixed(2)}`}>
                                            {imgInfo.Score?.toFixed(2)}
                                        </div>
                                    )}
                                    <img
                                        className="thumbnail-image"
                                        src={getLinkImage(imgInfo)}
                                        alt={convertImageTypeToName(imgInfo.Type) || `Thumbnail ${index + 1}`}
                                    />
                                    <div className="thumbnail-type-badge">
                                        {convertImageTypeToName(imgInfo.Type) || 'Unknown'}
                                    </div>
                                    {imgInfo.IsStandardize === 1 && (
                                        <div className="thumbnail-standardize-icon" title="Standardized Image">
                                            <div dangerouslySetInnerHTML={{ __html: svgStandardize }} />
                                        </div>
                                    )}

                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </>
    );
}